import { Fragment, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { RelocationFlowCity, RelocationFlowMovement } from "@/gql/schema";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslations } from "use-intl";

export type HeatMetric = "bookings" | "time";

interface Props {
  cities: RelocationFlowCity[];
  movements: RelocationFlowMovement[];
  heatMetric: HeatMetric;
}

function useMatrixData(
  cities: RelocationFlowCity[],
  movements: RelocationFlowMovement[],
) {
  const uniqueCities = useMemo(() => {
    const cityIds = new Set<string>();
    movements.forEach((m) => {
      cityIds.add(m.departure_city_id);
      cityIds.add(m.delivery_city_id);
    });
    return cities
      .filter((c) => cityIds.has(c.city_id))
      .sort((a, b) => a.city_name.localeCompare(b.city_name));
  }, [cities, movements]);

  const movementMap = useMemo(() => {
    const map = new Map<string, number>();
    movements.forEach((m) => {
      const key = `${m.departure_city_id}-${m.delivery_city_id}`;
      map.set(key, m.count);
    });
    return map;
  }, [movements]);

  const medianMap = useMemo(() => {
    const map = new Map<string, number | null | undefined>();
    movements.forEach((m) => {
      const key = `${m.departure_city_id}-${m.delivery_city_id}`;
      map.set(key, m.median_days_to_book);
    });
    return map;
  }, [movements]);

  return { uniqueCities, movementMap, medianMap };
}

export function useMatrixExport(
  cities: RelocationFlowCity[],
  movements: RelocationFlowMovement[],
  filename: string,
) {
  const { uniqueCities, movementMap } = useMatrixData(cities, movements);

  return useCallback(() => {
    const headers = [
      "Departure \\ Delivery",
      ...uniqueCities.map((c) => c.city_name),
    ];
    const rows = uniqueCities.map((rowCity) => {
      const cells = uniqueCities.map((colCity) => {
        if (rowCity.city_id === colCity.city_id) {
          return "";
        }
        return movementMap.get(`${rowCity.city_id}-${colCity.city_id}`) ?? 0;
      });
      return [rowCity.city_name, ...cells];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [uniqueCities, movementMap, filename]);
}

export function RelocationFlowMatrix({ cities, movements, heatMetric }: Props) {
  const t = useTranslations("report");
  const { uniqueCities, movementMap, medianMap } = useMatrixData(
    cities,
    movements,
  );
  const maxCount = Math.max(...movements.map((m) => m.count), 1);
  const maxMedian = Math.max(
    ...movements.map((m) => m.median_days_to_book ?? 0),
    1,
  );

  function medianLabel(median: number | null | undefined) {
    if (median === null || median === undefined) {
      return "—";
    }
    return t("dayCountShort", { count: median });
  }

  function getHeatStyle(count: number, median: number | null | undefined) {
    if (count === 0) {
      return undefined;
    }

    const intensity =
      heatMetric === "time"
        ? median !== null && median !== undefined
          ? median / maxMedian
          : 0
        : count / maxCount;

    if (intensity === 0) {
      return undefined;
    }

    const alpha = 0.06 + 0.34 * intensity;
    return { backgroundColor: `rgba(37, 99, 235, ${alpha})` };
  }

  const numCols = uniqueCities.length;
  const numRows = uniqueCities.length;

  if (uniqueCities.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500">
        {t("noMovementData")}
      </div>
    );
  }

  return (
    <div
      className="grid overflow-x-auto rounded-lg border"
      style={{
        gridTemplateColumns: `8rem repeat(${numCols}, minmax(4rem, 1fr))`,
        gridTemplateRows: `3rem repeat(${numRows}, 3.5rem)`,
      }}
    >
      {/* Top-left corner */}
      <div className="sticky left-0 top-0 z-20 flex items-center justify-center border-b border-r bg-gray-100 text-xs font-medium text-gray-500">
        Depart \ Deliver
      </div>

      {/* Column headers (delivery cities) */}
      {uniqueCities.map((city) => (
        <div
          key={`col-${city.city_id}`}
          className="sticky top-0 z-10 flex items-center border-b bg-gray-100 px-2 text-xs font-medium"
          title={city.city_name}
        >
          <span className="truncate">{city.city_name}</span>
        </div>
      ))}

      {/* Rows */}
      {uniqueCities.map((rowCity) => (
        <Fragment key={rowCity.city_id}>
          {/* Row header (departure city) */}
          <div
            className="sticky left-0 z-10 flex items-center border-r bg-gray-100 px-2 text-xs font-medium"
            title={rowCity.city_name}
          >
            <span className="truncate">{rowCity.city_name}</span>
          </div>

          {/* Cells */}
          {uniqueCities.map((colCity) => {
            const key = `${rowCity.city_id}-${colCity.city_id}`;
            const count = movementMap.get(key) ?? 0;
            const median = medianMap.get(key);
            const isDiagonal = rowCity.city_id === colCity.city_id;
            const hasValue = !isDiagonal && count > 0;

            const cell = (
              <div
                className={cn(
                  "flex h-full w-full flex-col items-center justify-center border-b border-r text-sm leading-tight",
                  isDiagonal ? "bg-gray-100" : "text-gray-900",
                )}
                style={isDiagonal ? undefined : getHeatStyle(count, median)}
              >
                {hasValue ? (
                  <span className="text-sm font-semibold">{count}</span>
                ) : null}
                {hasValue ? (
                  <span className="text-[10px] font-normal opacity-60">
                    {medianLabel(median)}
                  </span>
                ) : null}
              </div>
            );

            if (!hasValue) {
              return <Fragment key={key}>{cell}</Fragment>;
            }

            return (
              <Tooltip key={key}>
                <TooltipTrigger asChild>{cell}</TooltipTrigger>
                <TooltipContent className="text-center">
                  <div className="font-semibold">
                    {rowCity.city_name} → {colCity.city_name}
                  </div>
                  <div className="text-background/70">
                    {t("corridorBookings")}: {count}
                  </div>
                  <div className="text-background/70">
                    {t("medianDaysToBook")}:{" "}
                    {median !== null && median !== undefined
                      ? t("dayCount", { count: median })
                      : "—"}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}
